import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    Input,
    Select,
    Option,
    Chip,
    Textarea,
    IconButton,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
    Switch,
    Rating
} from "@material-tailwind/react";
import {
    Search,
    MoreVertical,
    Eye,
    Edit,
    UserCheck,
    AlertCircle,
    XCircle,
    MessageSquare,
    Paperclip,
    User,
    Mail,
    Phone,
    Tag,
    TrendingUp,
    RefreshCw,
    Settings,
    Plus,
    Zap,
    AlertTriangle,
    Info,
    X,
    Filter,
    Users,
    PlusIcon,
    UserIcon,
    Calendar,
    MapPinIcon
} from "lucide-react";
import {
    useAssignTicket,
    useCreateTicketByAdmin,
    useGetAllTickets,
    useUpdateTicketDueDate,
    useUpdateTicketStatus,
    useUpdateTicketLocation
} from '../../custom-hooks/use-support';
import { useGetUsers } from '../../custom-hooks/use-users';
import { STATIC_USER_ROLES, SUPPORT_TICKET_PRIORITIES_ENUM, SUPPORT_TICKET_STATUSES } from '../../utils/enums';
import { AttachmentPicker, ModalX, PageHeader } from '../../components';
import TicketLocationModal from './components/TicketLocationModal';
import LocationPicker from '../maintenance/components/LocationPicker';
import { SupportTicketStatusColumns } from '../../utils/ui-interpreters';
import Autocomplete from '../../components/autocomplete';
import { useGetSupportCategories } from '../../custom-hooks/use-support-categories';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useAuth } from '../../services/contexts/authContext';

moment.updateLocale('en-short', {
    relativeTime: {
        future: '%s',
        past: '%s',
        s: '1s',
        ss: '%ss',
        m: '1m',
        mm: '%dm',
        h: '1h',
        hh: '%dh',
        d: '1d',
        dd: '%dd',
        M: '1mo',
        MM: '%dmo',
        y: '1y',
        yy: '%dy',
    }
});


// Ticket card chips remount on every visit to this page (route swap, not a data reload),
// so we skip Material Tailwind's default mount/unmount fade to avoid a visible re-flash.
const NO_MOUNT_FADE = {
    mount: { opacity: 1, transition: { duration: 0 } },
    unmount: { opacity: 0, transition: { duration: 0 } },
};

const INITIAL_NEW_TICKET_FORM_DATA = {
    customer: null,
    agent: null,
    subject: '',
    description: '',
    category: '',
    priority: '',
    isInternal: true,
    attachments: [],
    branch: '',
    building: '',
    floor: '',
    space: '',
}

const CreateTicketModal = ({
    isOpen,
    onClose,
    customers,
    customersLoading,
    customersError,
    agents,
    agentsLoading,
    agentsError,
    createTicketByAdminMutation,
}) => {
    const { userDetails } = useAuth();
    const { data: categories = [] } = useGetSupportCategories();
    const [formData, setFormData] = useState(INITIAL_NEW_TICKET_FORM_DATA);

    React.useEffect(() => {
        if (isOpen) {
            setFormData({
                ...INITIAL_NEW_TICKET_FORM_DATA,
                customer: (userDetails && userDetails.name) ? {
                    value: userDetails._id,
                    label: <div className='flex items-center justify-between gap-2'>
                        <div className='flex items-center gap-2'>
                            <div className='w-4 h-4 rounded-full'>
                                <UserIcon size={12} />
                            </div>
                            {userDetails.name}
                        </div>
                        <div className='text-xs text-gray-500'>
                            {userDetails.email || ''}
                        </div>
                    </div>,
                    searchLabel: `${userDetails.name} (${userDetails.email || ''})`
                } : null
            });
        }
    }, [isOpen, userDetails]);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSelectChange = (name, value) => {
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                customerId: formData.customer.value,
                isInternal: formData.isInternal,
                subject: formData.subject,
                description: formData.description,
                category: formData.category,
                priority: formData.priority,
                attachments: formData.attachments,
                // Required on staff-created tickets so the dashboard's By Floor panel has
                // data from day one. Customer-raised tickets still arrive without it and
                // get located later via the status gate.
                branch: formData.branch,
                building: formData.building,
                floor: formData.floor,
                space: formData.space,
                ...(formData.agent && { agentId: formData.agent.value }),
            };
            await createTicketByAdminMutation.mutateAsync(payload);
            onClose();
        } catch (error) {
            console.error('Error creating ticket:', error);
        }
    };

    return (
        <ModalX isOpen={isOpen} onClose={onClose} title="Create Ticket" maxWidth="xl">
            <div className="space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                    <Typography variant="small" className="font-semibold text-gray-900">
                        Internal Ticket
                    </Typography>
                    <Switch
                        id="is-internal-ticket"
                        color="indigo"
                        checked={formData.isInternal}
                        onChange={(e) => setFormData({ ...formData, isInternal: e.target.checked, customer: null })}
                    />
                </div>
                {
                    formData.isInternal ? (
                        agentsError ? (
                            <div className='text-red-500'>
                                {agentsError.message}
                            </div>
                        ) : (
                            <Autocomplete
                                placeholder={<>Select Staff/Agent <span className="text-red-500 ml-1">*</span></>}
                                isLoading={agentsLoading}
                                options={
                                    agents?.users?.map((user) => ({
                                        value: user._id,
                                        label: <div className='flex items-center justify-between gap-2'>
                                            <div className='flex items-center gap-2'>
                                                <div className='w-4 h-4 rounded-full'>
                                                    <UserIcon size={12} />
                                                </div>
                                                {user.name}
                                            </div>
                                            <div className='text-xs text-gray-500'>
                                                {user.email}
                                            </div>
                                        </div>,
                                        searchLabel: user.name + ' (' + user.email + ')'
                                    })) || []}
                                isClearable={true}
                                required={true}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.searchLabel}
                                onChange={(newValue) => setFormData({ ...formData, customer: newValue })}
                                value={formData.customer}
                            />
                        )
                    ) : (
                        customersError ? (
                            <div className='text-red-500'>
                                {customersError.message}
                            </div>
                        ) : (
                            <Autocomplete
                                placeholder={<>Select Customer <span className="text-red-500 ml-1">*</span></>}
                                isLoading={customersLoading}
                                options={
                                    customers?.users?.map((user) => ({
                                        value: user._id,
                                        label: <div className='flex items-center justify-between gap-2'>
                                            <div className='flex items-center gap-2'>
                                                <div className='w-4 h-4 rounded-full'>
                                                    <UserIcon size={12} />
                                                </div>
                                                {user.name}
                                            </div>
                                            <div className='text-xs text-gray-500'>
                                                {user.email}
                                            </div>
                                        </div>,
                                        searchLabel: user.name + ' (' + user.email + ')'
                                    })) || []}
                                isClearable={true}
                                required={true}
                                getOptionLabel={(option) => option.label}
                                getOptionValue={(option) => option.searchLabel}
                                onChange={(newValue) => setFormData({ ...formData, customer: newValue })}
                                value={formData.customer}
                            />
                        )
                    )
                }
                {
                    agentsError ? (
                        <div className='text-red-500'>
                            {agentsError.message}
                        </div>
                    ) : (
                        <Autocomplete
                            placeholder="Assign Agent (Optional)"
                            isLoading={agentsLoading}
                            options={
                                agents?.users?.map((user) => ({
                                    value: user._id,
                                    label: <div className='flex items-center justify-between gap-2'>
                                        <div className='flex items-center gap-2'>
                                            <div className='w-4 h-4 rounded-full'>
                                                <UserIcon size={12} />
                                            </div>
                                            {user.name}
                                        </div>
                                        <div className='text-xs text-gray-500'>
                                            {user.email}
                                        </div>
                                    </div>,
                                    searchLabel: user.name + ' (' + user.email + ')'
                                })) || []}
                            isClearable={true}
                            getOptionLabel={(option) => option.label}
                            getOptionValue={(option) => option.searchLabel}
                            onChange={(newValue) => setFormData({ ...formData, agent: newValue })}
                            value={formData.agent}
                        />
                    )
                }
                <hr />
                <Input
                    name='subject'
                    label="Subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                />
                <Textarea
                    name='description'
                    label="Description"
                    required
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={6}
                />
                <hr />
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                    <div className='md:col-span-2'>
                        <Select
                            name='category'
                            label={<>Category <span className="text-red-500 ml-1">*</span></>}
                            value={formData.category}
                            onChange={(value) => handleSelectChange('category', value)}
                            // Without this the closed box renders the whole Option, description
                            // and all, which wraps to two lines. The description belongs in the
                            // open list only.
                            selected={() => categories.find((c) => c.slug === formData.category)?.name || ''}
                        >
                            {
                                categories.map((category) => (
                                    <Option key={category.slug} value={category.slug}>
                                        <div className='flex flex-col'>
                                            <span className='text-sm font-medium'>{category.name}</span>
                                            {category.description && (
                                                <span className='text-xs text-gray-500'>{category.description}</span>
                                            )}
                                        </div>
                                    </Option>
                                ))
                            }
                        </Select>
                    </div>
                    <div>
                        <Select
                            name='priority'
                            label={<>Priority <span className="text-red-500 ml-1">*</span></>}
                            value={formData.priority}
                            onChange={(value) => handleSelectChange('priority', value)}
                        >
                            {
                                SUPPORT_TICKET_PRIORITIES_ENUM.map((priority) => (
                                    <Option key={priority} value={priority}>
                                        <span className='capitalize'>
                                            {priority}
                                        </span>
                                    </Option>
                                ))
                            }
                        </Select>
                    </div>
                </div>
                <div>
                    <Typography variant="small" className="mb-2 font-semibold text-gray-900">
                        Location <span className="text-red-500">*</span>
                        <span className="ml-1 font-normal text-gray-500">(internal only)</span>
                    </Typography>
                    <LocationPicker
                        branch={formData.branch}
                        building={formData.building}
                        floor={formData.floor}
                        space={formData.space}
                        onChange={(patch) => setFormData((prev) => {
                            const next = { ...prev, ...patch };
                            if (patch.branch !== undefined && patch.branch !== prev.branch) { next.building = ''; next.floor = ''; }
                            if (patch.building !== undefined && patch.building !== prev.building) { next.floor = ''; }
                            return next;
                        })}
                        disabled={createTicketByAdminMutation.isLoading}
                        autoSelectSingleBranch
                    />
                </div>
                <div>
                    <Typography variant="small" className="mb-2 font-semibold text-gray-900">
                        Attachments
                    </Typography>
                    <AttachmentPicker
                        attachments={formData.attachments}
                        onChange={(attachments) => setFormData((prev) => ({ ...prev, attachments }))}
                        disabled={createTicketByAdminMutation.isLoading}
                        label="Add photos or documents"
                    />
                </div>
                <hr />
                <div className='flex items-center justify-end gap-2'>
                    <Button variant="outlined" className='rounded-full' size='sm' color="gray" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="gradient"
                        className='rounded-full'
                        size='sm'
                        onClick={handleSubmit}
                        disabled={
                            createTicketByAdminMutation.isLoading ||
                            !formData.customer ||
                            !formData.subject ||
                            !formData.description ||
                            !formData.category ||
                            !formData.priority ||
                            !formData.floor
                        }
                        loading={createTicketByAdminMutation.isLoading}
                    >
                        Create Ticket
                    </Button>
                </div>
            </div>
        </ModalX>
    );
};

const AssignAgentModal = ({ isOpen, onClose, ticket, agents, agentsLoading, assignTicketMutation }) => {
    const [selectedAgent, setSelectedAgent] = useState('');

    useEffect(() => {
        if (isOpen && ticket) {
            setSelectedAgent(ticket.assignedAgent?._id || '');
        }
    }, [isOpen, ticket]);

    const submitAssignment = async () => {
        if (!selectedAgent) return;
        try {
            await assignTicketMutation.mutateAsync({ ticketId: ticket._id, data: { agentId: selectedAgent } });
            onClose();
        } catch (error) {
            console.error('Error assigning agent:', error);
        }
    };

    return (
        <ModalX isOpen={isOpen} onClose={onClose} title="Assign Agent">
            <div>
                <Select
                    label="Select Agent"
                    value={selectedAgent}
                    onChange={(val) => setSelectedAgent(val)}
                    disabled={agentsLoading}
                >
                    {agents?.users?.map((agent) => (
                        <Option key={agent._id} value={agent._id}>
                            {agent.name} ({agent.email})
                        </Option>
                    ))}
                </Select>
            </div>
            <hr className='my-4' />
            <div className="flex items-center justify-end gap-2">
                <Button
                    variant="outlined"
                    color="gray"
                    onClick={() => {
                        onClose();
                        setSelectedAgent('');
                    }}
                    className="rounded-full flex items-center gap-2"
                    size="sm"
                    disabled={assignTicketMutation.isLoading}
                >
                    Cancel
                </Button>
                <Button
                    variant="filled"
                    color="gray"
                    onClick={submitAssignment}
                    disabled={assignTicketMutation.isLoading || !selectedAgent}
                    className="rounded-full flex items-center gap-2"
                    size="sm"
                >
                    {assignTicketMutation.isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                    Assign
                </Button>
            </div>
        </ModalX>
    );
};

const SetDueDateModal = ({ isOpen, onClose, ticket, updateTicketDueDateMutation }) => {
    const [dueDate, setDueDate] = useState(null);

    useEffect(() => {
        if (isOpen && ticket) {
            setDueDate(ticket.dueDate);
        }
    }, [isOpen, ticket]);

    const submitSetDueDate = async () => {
        if (!dueDate) return;
        try {
            await updateTicketDueDateMutation.mutateAsync({ ticketId: ticket._id, data: { dueDate } });
            onClose();
        } catch (error) {
            console.error('Error setting due date:', error);
        }
    };

    return (
        <ModalX isOpen={isOpen} onClose={onClose} title="Set Due Date">
            <div className="pt-2">
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                        label="Due Date"
                        value={dueDate ? moment(dueDate) : null}
                        onChange={(newValue) => setDueDate(newValue)}
                        slotProps={{ 
                            textField: { fullWidth: true },
                            popper: { sx: { zIndex: 99999 } }
                        }}
                    />
                </LocalizationProvider>
            </div>
            <hr className='my-4' />
            <div className="flex items-center justify-end gap-2">
                <Button
                    variant="outlined"
                    color="gray"
                    onClick={() => {
                        onClose();
                        setDueDate(null);
                    }}
                    className="rounded-full flex items-center gap-2"
                    size="sm"
                    disabled={updateTicketDueDateMutation.isLoading}
                >
                    Cancel
                </Button>
                <Button
                    variant="filled"
                    color="gray"
                    onClick={submitSetDueDate}
                    disabled={!dueDate || updateTicketDueDateMutation.isLoading}
                    className="rounded-full flex items-center gap-2"
                    size="sm"
                >
                    {updateTicketDueDateMutation.isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                    Save Due Date
                </Button>
            </div>
        </ModalX>
    );
};

const UpdateStatusModal = ({ isOpen, onClose, ticket, statusColumns, updateTicketStatusMutation, onLocationRequired }) => {
    const [newStatus, setNewStatus] = useState('');
    const [resolution, setResolution] = useState('');

    useEffect(() => {
        if (isOpen && ticket) {
            setNewStatus(ticket.status);
            setResolution(ticket.resolution || '');
        }
    }, [isOpen, ticket]);

    const submitStatusUpdate = async () => {
        if (!newStatus) return;
        try {
            // Same gate as the detail page — a ticket needs a location before its status
            // can move. Hand off rather than firing a request the server will reject.
            if (!ticket?.floor) {
                onLocationRequired({ status: newStatus, resolution: resolution || '' });
                return;
            }
            await updateTicketStatusMutation.mutateAsync({ ticketId: ticket._id, data: { status: newStatus, resolution: resolution || '' } });
            onClose();
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    return (
        <ModalX isOpen={isOpen} onClose={onClose} title="Update Ticket Status">
            <div className="space-y-4">
                <Select
                    label="New Status"
                    value={newStatus}
                    onChange={(value) => setNewStatus(value)}
                >
                    {statusColumns.map((status) => (
                        <Option key={status.id} value={status.id}>
                            <div className="flex items-center gap-2">
                                <status.icon className={`h-4 w-4 text-${status.color}-600`} />
                                <span className="capitalize">{status.title.replace('_', ' ')}</span>
                            </div>
                        </Option>
                    ))}
                </Select>

                {(newStatus === 'resolved' || newStatus === 'closed') && (
                    <Textarea
                        label="Resolution (Optional)"
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        rows={4}
                    />
                )}
            </div>
            <hr className='my-4' />
            <div className="flex items-center justify-end gap-2">
                <Button
                    variant="outlined"
                    color="gray"
                    onClick={() => {
                        onClose();
                        setNewStatus('');
                        setResolution('');
                    }}
                    className="rounded-full flex items-center gap-2"
                    size="sm"
                    disabled={updateTicketStatusMutation.isLoading}
                >
                    Cancel
                </Button>
                <Button
                    variant="filled"
                    color="gray"
                    onClick={submitStatusUpdate}
                    disabled={!newStatus || updateTicketStatusMutation.isLoading}
                    className="rounded-full flex items-center gap-2"
                    size="sm"
                >
                    {updateTicketStatusMutation.isLoading && <RefreshCw className="h-4 w-4 animate-spin" />}
                    Update Status
                </Button>
            </div>
        </ModalX>
    );
};

    const getPriorityColor = (priority) => {
        const colors = {
            low: 'text-green-600',
            medium: 'text-orange-600',
            high: 'text-red-600',
            urgent: 'text-purple-600'
        };
        return colors[priority] || 'text-gray-600';
    };

    const getPriorityBg = (priority) => {
        const colors = {
            low: 'bg-green-100',
            medium: 'bg-orange-100',
            high: 'bg-red-100',
            urgent: 'bg-purple-100'
        };
        return colors[priority] || 'bg-gray-100';
    };

    const getPriorityIcon = (priority) => {
        const icons = {
            low: Info,
            medium: AlertTriangle,
            high: AlertCircle,
            urgent: Zap
        };
        return icons[priority] || Info;
    };

    const getCategoryIcon = (category) => {
        const icons = {
            technical: Settings,
            billing: TrendingUp,
            general: MessageSquare,
            account: User,
            bug_report: XCircle,
            feature_request: Plus,
            other: Tag
        };
        return icons[category] || Tag;
    };

    const getTimeAgo = (date) => {
        return moment(date).fromNow();
    };


    const TicketCard = ({ ticket, handlers }) => {
        const { handleOpenTicketPage, handleViewTicket, handleUpdateStatus, handleAssignTicket, handleSetDueDate, handleSetLocation } = handlers;
        const PriorityIcon = getPriorityIcon(ticket.priority);
        const CategoryIcon = getCategoryIcon(ticket.category);

        const dueDate = ticket.dueDate ? moment(ticket.dueDate).format('MMM Do, YY') : null;
        const ifDueDateIsPast = dueDate ? moment(dueDate).isBefore(moment()) : false;
        const dueDateColor = [
            SUPPORT_TICKET_STATUSES.RESOLVED,
            SUPPORT_TICKET_STATUSES.CLOSED,
            SUPPORT_TICKET_STATUSES.CANCELLED
        ].includes(ticket.status) ? 'gray' : (ifDueDateIsPast ? 'red' : 'blue');

        const dueDateBgColor = ifDueDateIsPast ? 'bg-red-100' : 'bg-blue-100';

        return (
            <div
                key={ticket._id}
                className={`bg-white hover:bg-gray-50 border ${ticket.isInternal ? 'border-indigo-300 shadow-sm shadow-indigo-100' : 'border-gray-200'} rounded-lg p-4 mb-3 hover:border-gray-300 transition-all duration-300 cursor-pointer`}
                onClick={() => handleOpenTicketPage(ticket)}
            >
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                        <PriorityIcon className={`h-4 w-4 ${getPriorityColor(ticket.priority)}`} />
                        <CategoryIcon className="h-4 w-4 text-gray-500" />
                        <span className="text-xs font-mono text-gray-500 uppercase">{ticket._id.slice(ticket._id.length - 5, ticket._id.length)}</span>
                        {
                            ticket.dueDate && (
                                <Chip
                                    value={`Due: ${dueDate}`}
                                    className={`px-2 py-1 rounded text-xs font-medium capitalize ${dueDateBgColor} ${dueDateColor} cursor-pointer`}
                                    variant='ghost'
                                    size='sm'
                                    color={dueDateColor}
                                    animate={NO_MOUNT_FADE} />
                            )}
                    </div>
                    <Menu>
                        <MenuHandler>
                            <IconButton
                                variant="text"
                                size="sm"
                                className="h-6 w-6"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <MoreVertical className="h-3 w-3" />
                            </IconButton>
                        </MenuHandler>
                        <MenuList className="border border-gray-200" onClick={(e) => e.stopPropagation()}>
                            <MenuItem
                                className="flex items-center gap-2 text-sm focus:outline-none"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleViewTicket(ticket);
                                }}
                            >
                                <Eye className="h-4 w-4" />
                                View Details
                            </MenuItem>
                            <MenuItem className="flex items-center gap-2 text-sm" onClick={(e) => { e.stopPropagation(); handleUpdateStatus(ticket); }}>
                                <Edit className="h-4 w-4" />
                                Update Status
                            </MenuItem>
                            <MenuItem className="flex items-center gap-2 text-sm" onClick={(e) => { e.stopPropagation(); handleAssignTicket(ticket); }}>
                                <UserCheck className="h-4 w-4" />
                                Assign Agent
                            </MenuItem>
                            <MenuItem className="flex items-center gap-2 text-sm" onClick={(e) => { e.stopPropagation(); handleSetLocation(ticket); }}>
                                <MapPinIcon className="h-4 w-4" />
                                {ticket.floor ? 'Edit Location' : 'Set Location'}
                            </MenuItem>
                            {
                                [SUPPORT_TICKET_STATUSES.OPEN, SUPPORT_TICKET_STATUSES.IN_PROGRESS].includes(ticket.status) && (
                                    <MenuItem className="flex items-center gap-2 text-sm" onClick={(e) => { e.stopPropagation(); handleSetDueDate(ticket); }}>
                                        <Calendar className="h-4 w-4" />
                                        Set Due Date
                                    </MenuItem>
                                )
                            }
                        </MenuList>
                    </Menu>
                </div>

                <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2 leading-tight hover:underline">
                    {ticket.subject}
                </h3>

                <div className="flex items-center gap-2 mb-3">
                    <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-blue-600 font-medium text-xs">
                            {ticket.customer?.name?.charAt(0) || '?'}
                        </span>
                    </div>
                    <span className="text-xs text-gray-600 truncate capitalize">
                        {ticket.customer?.name || 'Deleted Customer'}
                    </span>
                </div>

                <div className="flex items-center gap-2 mb-3">
                    <Chip
                        value={ticket.priority}
                        className={`px-2 py-1 rounded text-xs font-medium capitalize ${getPriorityBg(ticket.priority)} ${getPriorityColor(ticket.priority)} cursor-pointer`}
                        animate={NO_MOUNT_FADE}
                    />
                    <Chip
                        value={ticket.category.replace('_', ' ')}
                        className="px-2 py-1 rounded text-xs font-medium capitalize" variant='outlined' color='teal'
                        animate={NO_MOUNT_FADE} />
                    {ticket.isInternal && (
                        <Chip
                            value="Internal"
                            className="px-2 py-1 rounded text-xs font-medium uppercase border-indigo-200 bg-indigo-50 text-indigo-700" variant='ghost'
                            animate={NO_MOUNT_FADE} />
                    )}
                </div>

                {ticket.customerSatisfactionRating && (
                    <div className="mb-3 flex items-center">
                        <Rating value={ticket.customerSatisfactionRating} readonly className="flex gap-1 items-center" />
                    </div>
                )}

                <div className='flex items-center gap-2 mb-1'>
                    {ticket?.attachments?.length > 0 && (
                        <div className="flex items-center gap-1">
                            <Paperclip className="h-3 w-3 text-gray-400" />
                            <span className="text-xs text-gray-500">{ticket.attachments.length}</span>
                        </div>
                    )}
                    {ticket?.requiresCustomerAcknowledgment && (
                        <AlertCircle className="h-3 w-3 text-orange-500" />
                    )}
                    {ticket?.escalationLevel > 0 && (
                        <TrendingUp className="h-3 w-3 text-red-500" />
                    )}
                </div>
                <div className="text-xs text-gray-500">
                    {ticket.resolutionDate ? (
                        <span className="text-green-500">
                            {getTimeAgo(ticket.resolutionDate)} - Resolved
                        </span>
                    ) : (
                        <span>
                            Last Activity: {getTimeAgo(ticket.lastUpdated)}
                        </span>
                    )}
                </div>

                <hr className='my-2' />
                <div className="flex items-center justify-between border-gray-100">
                    <div className="text-xs text-gray-500">
                        <span>
                            {getTimeAgo(ticket.createdDate)}
                        </span>
                    </div>
                    <div className="text-xs text-gray-400">
                        {ticket.assignedAgent ? (
                            <div className="flex items-center gap-2">
                                <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                                    <span className="text-green-600 font-medium text-xs">
                                        {ticket.assignedAgent.name.charAt(0)}
                                    </span>
                                </div>
                                <span className="text-xs text-gray-600">
                                    {ticket.assignedAgent.name}
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Users className="h-4 w-4 text-gray-400" />
                                <span className="text-xs text-gray-400">Unassigned</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const StatusColumn = ({ column, tickets, isLoading, handlers }) => {
        const ColumnIcon = column.icon;
        const scrollContainerRef = useRef(null);
        const hasRestoredScroll = useRef(false);
        const scrollSaveFrame = useRef(null);

        // Restore this column's scroll position once its tickets have loaded/rendered
        useLayoutEffect(() => {
            if (isLoading || hasRestoredScroll.current || !scrollContainerRef.current) return;
            const savedScrollTop = sessionStorage.getItem(`support-kanban-scroll-${column.id}`);
            if (savedScrollTop) {
                scrollContainerRef.current.scrollTop = parseInt(savedScrollTop, 10);
            }
            hasRestoredScroll.current = true;
        }, [isLoading, column.id]);

        useEffect(() => () => {
            if (scrollSaveFrame.current) cancelAnimationFrame(scrollSaveFrame.current);
        }, []);

        // Coalesce rapid scroll events to one write per frame instead of one per event
        const handleColumnScroll = (e) => {
            const scrollTop = e.currentTarget.scrollTop;
            if (scrollSaveFrame.current) cancelAnimationFrame(scrollSaveFrame.current);
            scrollSaveFrame.current = requestAnimationFrame(() => {
                sessionStorage.setItem(`support-kanban-scroll-${column.id}`, scrollTop.toString());
            });
        };

        return (
            <div className="flex-1 min-w-80">
                <div
                    ref={scrollContainerRef}
                    onScroll={handleColumnScroll}
                    className={`bg-white border border-gray-200 rounded-lg ${column.bgColor} h-full max-h-[800px] overflow-y-auto relative`}
                >
                    <div className={`p-4 border-b ${column.borderColor} sticky top-0 bg-white z-10`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <ColumnIcon className={`h-5 w-5 text-${column.color}-600`} />
                                <div>
                                    <h3 className="font-semibold text-gray-900">{column.title}</h3>
                                    <p className="text-sm text-gray-600">{tickets?.length} tickets</p>
                                </div>
                            </div>
                            <div className={`px-2 py-1 rounded-full text-sm font-medium bg-${column.color}-100 text-${column.color}-700`}>
                                {tickets?.length}
                            </div>
                        </div>
                    </div>

                    <div className="p-4 h-100 overflow-y-auto">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                            </div>
                        ) : tickets.length === 0 ? (
                            <div className="text-center py-8">
                                <ColumnIcon className={`h-12 w-12 text-${column.color}-200 mx-auto mb-3`} />
                                <p className="text-sm text-gray-500">No tickets</p>
                                <p className="text-xs text-gray-400">{column.description}</p>
                            </div>
                        ) : (
                            tickets.map((ticket) => (
                                <TicketCard key={ticket._id} ticket={ticket} handlers={handlers} />
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    };

const SupportTicketsKanban = () => {
    const navigate = useNavigate();

    const { data: tickets, isLoading, error, refetch: refetchTickets } = useGetAllTickets({
        _page: 1,
        _limit: 500
    });

    const { data: agents, isLoading: agentsLoading, error: agentsError, } = useGetUsers({
        role: [STATIC_USER_ROLES.ADMIN, STATIC_USER_ROLES.MANAGER, STATIC_USER_ROLES.EMPLOYEE, STATIC_USER_ROLES.STAFF],
        _page: 1,
        _limit: 1000
    });
    const { data: customers, isLoading: customersLoading, error: customersError } = useGetUsers({
        role: [STATIC_USER_ROLES.USER, STATIC_USER_ROLES.TEAM_MEMBER],
        _page: 1,
        _limit: 1000
    });

    const [searchTerm, setSearchTerm] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const { data: categories = [] } = useGetSupportCategories();
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [assignedAgentFilter, setAssignedAgentFilter] = useState('all');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [assignDialogOpen, setAssignDialogOpen] = useState(false);
    const [createTicketModalOpen, setCreateTicketModalOpen] = useState(false);
    const [dueDateDialogOpen, setDueDateDialogOpen] = useState(false);
    const [locationDialogOpen, setLocationDialogOpen] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState(null);
    const [showFilters, setShowFilters] = useState(false);

    const updateTicketStatusMutation = useUpdateTicketStatus();
    const updateTicketLocationMutation = useUpdateTicketLocation();
    const assignTicketMutation = useAssignTicket();
    const createTicketByAdminMutation = useCreateTicketByAdmin();
    const updateTicketDueDateMutation = useUpdateTicketDueDate();

    const statusColumns = Object.keys(SupportTicketStatusColumns).map((status) => {
        const statusConfig = SupportTicketStatusColumns[status];
        return {
            id: status,
            title: statusConfig.label,
            ...statusConfig
        }
    })

    const handleViewTicket = (ticket) => {
        setSelectedTicket(ticket);
        setViewDialogOpen(true);
    };

    const handleOpenTicketPage = (ticket) => {
        navigate(`/support/${ticket._id}`);
    };

    const handleUpdateStatus = (ticket) => {
        setSelectedTicket(ticket);
        setStatusDialogOpen(true);
    };

    const handleSetLocation = (ticket) => {
        setSelectedTicket(ticket);
        setPendingStatusChange(null);
        setLocationDialogOpen(true);
    };

    const handleLocationRequired = (pending) => {
        setStatusDialogOpen(false);
        setPendingStatusChange(pending);
        setLocationDialogOpen(true);
    };

    const closeLocationDialog = () => {
        setLocationDialogOpen(false);
        setPendingStatusChange(null);
    };

    const submitTicketLocation = async (location) => {
        if (!selectedTicket?._id) return;
        if (pendingStatusChange) {
            // One atomic request: location and status together.
            await updateTicketStatusMutation.mutateAsync({
                ticketId: selectedTicket._id,
                data: { ...pendingStatusChange, ...location },
            });
        } else {
            await updateTicketLocationMutation.mutateAsync({ ticketId: selectedTicket._id, data: location });
        }
        closeLocationDialog();
    };

    const handleAssignTicket = (ticket) => {
        setSelectedTicket(ticket);
        setAssignDialogOpen(true);
    };

    const handleSetDueDate = (ticket) => {
        setSelectedTicket(ticket);
        setDueDateDialogOpen(true);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setPriorityFilter('all');
        setCategoryFilter('all');
        setAssignedAgentFilter('all');
    };

    const hasActiveFilters = searchTerm || priorityFilter !== 'all' || categoryFilter !== 'all' || assignedAgentFilter !== 'all';

    const getFilteredTickets = () => {
        let filtered = tickets?.tickets || [];

        if (searchTerm) {
            filtered = filtered.filter(ticket =>
                ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket.customer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                ticket._id.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (priorityFilter !== 'all') {
            filtered = filtered.filter(ticket => ticket.priority === priorityFilter);
        }

        if (categoryFilter !== 'all') {
            filtered = filtered.filter(ticket => ticket.category === categoryFilter);
        }

        if (assignedAgentFilter !== 'all') {
            if (assignedAgentFilter === 'unassigned') {
                filtered = filtered.filter(ticket => !ticket.assignedAgent);
            } else {
                filtered = filtered.filter(ticket => ticket.assignedAgent?._id === assignedAgentFilter);
            }
        }

        return filtered;
    };

    const getTicketsByStatus = (status) => {
        return getFilteredTickets().filter(ticket => ticket.status === status);
    };


    if (error) {
        return <div>Error: {error.message}</div>;
    }

    const handleCreateTicket = () => {
        setCreateTicketModalOpen(true);
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="w-full space-y-6">
                <PageHeader
                    title="Support Tickets"
                    isLoading={isLoading}
                    actionType="custom"
                    customAction={
                        <div className="flex items-center justify-end gap-3">
                            <Button
                                variant="filled"
                                size="sm"
                                className="flex items-center gap-2 rounded-full px-2 md:px-4"
                                onClick={handleCreateTicket}
                                color="blue"
                            >
                                <PlusIcon className="h-4 w-4" />
                                <span className="hidden md:block">Create Ticket</span>
                            </Button>

                            <Button
                                variant="outlined"
                                size="sm"
                                className="flex items-center gap-2 border-gray-300 rounded-full px-2 md:px-4"
                                onClick={() => refetchTickets()}
                                disabled={isLoading}
                            >
                                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                                <span className="hidden md:block">Refresh</span>
                            </Button>
                            <Button
                                variant="filled"
                                size="sm"
                                className="flex items-center gap-2 rounded-full px-2 md:px-4"
                                onClick={() => setShowFilters(!showFilters)}
                                color={hasActiveFilters ? "blue" : "gray"}
                            >
                                <Filter className="h-4 w-4" />
                                <span className="hidden md:block">Filters</span>
                            </Button>
                        </div>
                    }
                />

                {showFilters && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Input
                                type="text"
                                label="Search tickets..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                icon={<Search className="h-4 w-4" />}
                            />
                            <Select
                                label="Priority"
                                value={priorityFilter}
                                onChange={(value) => setPriorityFilter(value)}
                            >
                                <Option value="all">All Priorities</Option>
                                <Option value="low">Low</Option>
                                <Option value="medium">Medium</Option>
                                <Option value="high">High</Option>
                                <Option value="urgent">Urgent</Option>
                            </Select>
                            <Select
                                label="Category"
                                value={categoryFilter}
                                onChange={(value) => setCategoryFilter(value)}
                                selected={() => (categoryFilter === 'all'
                                    ? 'All Categories'
                                    : categories.find((c) => c.slug === categoryFilter)?.name || categoryFilter)}
                            >
                                <Option value="all">All Categories</Option>
                                {categories.map((category) => (
                                    <Option key={category.slug} value={category.slug}>{category.name}</Option>
                                ))}
                            </Select>
                            <Select
                                label="Agent"
                                value={assignedAgentFilter}
                                onChange={(value) => setAssignedAgentFilter(value)}
                                disabled={agentsLoading}
                                className={agentsError ? 'border-red-500' : ''}
                            >
                                <Option value="all">All Agents</Option>
                                <Option value="unassigned">Unassigned</Option>
                                {agents?.users?.map((agent) => (
                                    <Option key={agent._id} value={agent._id}>
                                        {agent.name}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                        {hasActiveFilters && (
                            <div className="mt-4 flex items-center justify-between">
                                <p className="text-sm text-gray-600">
                                    {getFilteredTickets().length} of {tickets.length} tickets shown
                                </p>
                                <Button
                                    variant="text"
                                    size="sm"
                                    onClick={clearFilters}
                                    className="flex items-center gap-2"
                                >
                                    <X className="h-4 w-4" />
                                    Clear Filters
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex gap-6 overflow-x-auto pb-4">
                    {statusColumns.map((column) => (
                        <StatusColumn
                            key={column.id}
                            column={column}
                            tickets={getTicketsByStatus(column.id)}
                        isLoading={isLoading}
                            handlers={{ handleOpenTicketPage, handleViewTicket, handleUpdateStatus, handleAssignTicket, handleSetDueDate, handleSetLocation }}
                        />
                    ))}
                </div>

                <ModalX isOpen={viewDialogOpen} onClose={() => setViewDialogOpen(false)} title="Ticket Details" maxWidth="xxxl">
                    {selectedTicket && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Subject
                                    </Typography>
                                    <Typography variant="small" className="text-gray-600">
                                        {selectedTicket.subject}
                                    </Typography>
                                </div>
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Status
                                    </Typography>
                                    <div className="flex">
                                        <Chip
                                            value={selectedTicket.status.replace('_', ' ')}
                                            color={statusColumns.find(col => col.id === selectedTicket.status)?.color || 'gray'}
                                            size="sm"
                                            className="capitalize mt-1"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <Typography variant="small" className="font-semibold text-gray-900">
                                    Description
                                </Typography>
                                <Typography variant="small" className="text-gray-600 mt-1">
                                    {selectedTicket.description}
                                </Typography>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        {selectedTicket.isInternal ? "User" : "Customer"}
                                    </Typography>
                                    {selectedTicket.customer ? (
                                        <div className="mt-1 space-y-1">
                                            <div className="flex items-center gap-2">
                                                <User className="h-4 w-4 text-gray-500" />
                                                <Typography variant="small" className="text-gray-600">
                                                    {selectedTicket.customer.name}
                                                </Typography>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Mail className="h-4 w-4 text-gray-500" />
                                                <Typography variant="small" className="text-gray-600">
                                                    {selectedTicket.customer.email}
                                                </Typography>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Phone className="h-4 w-4 text-gray-500" />
                                                <Typography variant="small" className="text-gray-600">
                                                    {selectedTicket.customer.phone}
                                                </Typography>
                                            </div>
                                        </div>
                                    ) : (
                                        <Typography variant="small" className="text-gray-400 italic">
                                            User Deleted
                                        </Typography>
                                    )}
                                </div>
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Assigned Agent
                                    </Typography>
                                    <Typography variant="small" className="text-gray-600 mt-1">
                                        {selectedTicket.assignedAgent ? selectedTicket.assignedAgent.name : 'Unassigned'}
                                    </Typography>
                                </div>
                            </div>

                            {selectedTicket.resolution && (
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Resolution
                                    </Typography>
                                    <Typography variant="small" className="text-gray-600 mt-1">
                                        {selectedTicket.resolution}
                                    </Typography>
                                </div>
                            )}

                            {selectedTicket.attachments.length > 0 && (
                                <div>
                                    <Typography variant="small" className="font-semibold text-gray-900">
                                        Attachments
                                    </Typography>
                                    <div className="mt-1 space-y-1">
                                        {selectedTicket.attachments.map((attachment, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                                <Paperclip className="h-4 w-4 text-gray-500" />
                                                <Typography variant="small" className="text-blue-600 hover:underline cursor-pointer">
                                                    {attachment.originalName}
                                                </Typography>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </ModalX>

                <UpdateStatusModal
                    isOpen={statusDialogOpen}
                    onClose={() => setStatusDialogOpen(false)}
                    ticket={selectedTicket}
                    statusColumns={statusColumns}
                    updateTicketStatusMutation={updateTicketStatusMutation}
                onLocationRequired={handleLocationRequired}
                />

                <AssignAgentModal
                    isOpen={assignDialogOpen}
                    onClose={() => setAssignDialogOpen(false)}
                    ticket={selectedTicket}
                    agents={agents}
                    agentsLoading={agentsLoading}
                    assignTicketMutation={assignTicketMutation}
                />

                <SetDueDateModal
                    isOpen={dueDateDialogOpen}
                    onClose={() => setDueDateDialogOpen(false)}
                    ticket={selectedTicket}
                    updateTicketDueDateMutation={updateTicketDueDateMutation}
                />

                <TicketLocationModal
                isOpen={locationDialogOpen}
                onClose={closeLocationDialog}
                ticket={selectedTicket}
                onConfirm={submitTicketLocation}
                isLoading={updateTicketLocationMutation.isLoading || updateTicketStatusMutation.isLoading}
                title={pendingStatusChange ? 'Location Required' : 'Set Ticket Location'}
                description={pendingStatusChange
                    ? 'This ticket needs a location before its status can change. Set it below and the status will update at the same time.'
                    : undefined}
                confirmLabel={pendingStatusChange ? 'Save & Update Status' : 'Save Location'}
            />

            <CreateTicketModal
                    isOpen={createTicketModalOpen}
                    onClose={() => setCreateTicketModalOpen(false)}
                    customers={customers}
                    customersLoading={customersLoading}
                    customersError={customersError}
                    agents={agents}
                    agentsLoading={agentsLoading}
                    agentsError={agentsError}
                    createTicketByAdminMutation={createTicketByAdminMutation}
                />

                {!isLoading && getFilteredTickets().length === 0 && hasActiveFilters && (
                    <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                        <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
                        <p className="text-gray-600 mb-4">Try adjusting your search or filter criteria</p>
                        <Button variant="outlined" onClick={clearFilters}>
                            Clear all filters
                        </Button>
                    </div>
                )}

                {/* Empty State */}
                {!isLoading && tickets.length === 0 && (
                    <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                        <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No support tickets</h3>
                        <p className="text-gray-600">Support tickets will appear here when customers submit them</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupportTicketsKanban;